import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, CircularProgress, Chip } from '@mui/material'
import { fetchMapImage } from 'services/api/time-attendance'

const MapImage = ({
  latitude,
  longitude,
  width = 720,
  height = 280,
  zoom = 16,
  title = 'Location Map',
  markerLabel = 'Your location',
  showSourceChip = true,
  sx,
}) => {
  const [mapData, setMapData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const lat = useMemo(() => {
    if (latitude == null || latitude === '') return null
    const n = typeof latitude === 'string' ? Number(latitude) : latitude
    return Number.isFinite(n) ? n : null
  }, [latitude])

  const lon = useMemo(() => {
    if (longitude == null || longitude === '') return null
    const n = typeof longitude === 'string' ? Number(longitude) : longitude
    return Number.isFinite(n) ? n : null
  }, [longitude])

  const canLoad = lat != null && lon != null

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!canLoad) {
        setMapData(null)
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const result = await fetchMapImage(lat, lon, width, height, zoom)

        if (cancelled) return

        if (result?.success && result?.imageData && result?.mimeType) {
          setMapData(result)
        } else {
          setMapData(null)
          setError(result?.error || 'Failed to load map image')
        }
      } catch (e) {
        if (cancelled) return
        setMapData(null)
        setError(e?.message || 'Failed to load map image')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [canLoad, lat, lon, width, height, zoom])

  if (!canLoad) return null

  if (loading) {
    return (
      <Box
        sx={{
          mt: 1.5,
          width: '100%',
          maxWidth: `${width}px`,
          mx: 'auto',
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'grey.300',
          ...(sx || {}),
        }}
      >
        <CircularProgress size={24} sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Loading map...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          mt: 1.5,
          width: '100%',
          maxWidth: `${width}px`,
          mx: 'auto',
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'grey.300',
          px: 2,
          ...(sx || {}),
        }}
      >
        <Typography variant="caption" color="error" sx={{ mb: 0.5 }}>
          Map unavailable
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {error}
        </Typography>
      </Box>
    )
  }

  if (!mapData) return null

  return (
    <Box
      sx={{
        mt: 1.5,
        width: '100%',
        maxWidth: `${width}px`,
        mx: 'auto',
        ...(sx || {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'red',
                border: '1px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {markerLabel}
            </Typography>
          </Box>
        </Box>

        {showSourceChip && (
          <Chip
            label={mapData.source === 'tomtom' ? 'TomTom' : 'OpenStreetMap'}
            size="small"
            color={mapData.source === 'tomtom' ? 'primary' : 'default'}
            variant="outlined"
          />
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          borderRadius: '10px',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: 'white',
          boxShadow: '0 2px 12px rgba(15, 23, 42, 0.06)',
        }}
      >
        <img
          src={`data:${mapData.mimeType};base64,${mapData.imageData}`}
          alt={`Map of location at ${lat}, ${lon}`}
          style={{
            width: '100%',
            height,
            objectFit: 'cover',
            display: 'block',
          }}
          onError={() => setError('Failed to display map image')}
        />

        {/* Coordinates overlay (bottom-right) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            backgroundColor: 'rgba(0,0,0,0)',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'red',
                border: '1px solid white',
              }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white' }}>
              {lat.toFixed(6)}, {lon.toFixed(6)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {`Zoom level: ${zoom} | ${width} x ${height}px`}
      </Typography>
    </Box>
  )
}

export default MapImage
